/**
 * ZOOM SULLA DEDICA (richiesta di Davide, 26 set 2026): quando il cliente scrive l'incisione, la camera
 * inquadra da vicino la PIASTRINA (dove sta la scritta), non tutto il gioiello con la catena.
 *
 * Come: dal riquadro che contiene tutta la scena (sceneTree.root.boundingBox) si prende solo la parte bassa,
 * alta quanto e' larga (la piastrina e' in basso, la catena/il gancio/il perno stanno sopra), e si chiama
 * camera.zoomTo(riquadro). Per l'anello il riquadro e' quasi quadrato: si vede tutto l'anello.
 *
 * Il visore, a ogni aggiornamento del modello (ogni lettera scritta = aggiornamento), ri-inquadra da solo
 * TUTTA la scena ("Camera adjust"): finche' la modalita' dedica e' attiva, all'evento "bounding box cambiato"
 * si rifa' lo zoom sulla piastrina poco dopo.
 */
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useShapeDiverStoreViewport} from "@AppBuilderLib/entities/viewport/model/useShapeDiverStoreViewport";
import {addListener, Box, EVENTTYPE, EVENTTYPE_CAMERA, removeListener, sceneTree} from "@shapediver/viewer.session";
import {vec3} from "gl-matrix";
import {schermoTelefono} from "./tabsTelefono";

/** quanto stretta e' l'inquadratura sulla piastrina (1 = riquadro esatto; meno = piu' vicino) */

let attivo = false;
let tokenAscolto: string | null = null;
let tokenAscolto2: string | null = null;
let timer: number | undefined;

type Camera = {
	zoomToFactor?: number;
	zoomTo?: (box?: unknown, options?: unknown) => Promise<boolean>;
};

function camere(): Camera[] {
	try {
		return Object.values(useShapeDiverStoreViewport.getState().viewports).map(
			(vp) => vp.camera as unknown as Camera,
		);
	} catch {
		return [];
	}
}

/* ---- misura della piastrina: dai contorni in public/forme.json (millimetri), scelti in base a Modello/Forma/Misura */
const MODELLI: Record<string, string> = {
	Collana: "collana",
	Bracciale: "bracciale",
	Anello: "anello",
	"Orecchino a lobo": "lobo",
	"Orecchino pendente": "pendente",
};
let forme: Record<string, number[][]> | null = null;
fetch("forme.json")
	.then((r) => r.json())
	.then((j) => {
		forme = j;
	})
	.catch(() => {
		forme = null;
	});

/** la voce scelta in un menu del modello, come testo (es. "Collana") */
function scelta(nome: string): string {
	try {
		const {parameterStores, getParameter} = useShapeDiverStoreParameters.getState();
		for (const ns of Object.keys(parameterStores)) {
			const st = getParameter(ns, nome);
			if (!st) continue;
			const p = st.getState() as {state: {uiValue?: unknown}; definition: {choices?: string[]}};
			const v = String(p.state.uiValue ?? "");
			const scelte = p.definition.choices;
			if (scelte && scelte.length) {
				const i = parseInt(v, 10);
				return isNaN(i) ? v : (scelte[i] ?? v);
			}
			return v;
		}
	} catch {
		/* ignora */
	}
	return "";
}

/** {tipo, larghezza, altezza} della piastrina in mm, oppure null se non si sa */
function misuraPiastrina(): {tipo: string; larg: number; alt: number} | null {
	const tipo = MODELLI[scelta("Modello")] || "collana";
	if (!forme) return null;
	let f = scelta("Forma").toLowerCase();
	if (!f || f.startsWith("—") || f.startsWith("-")) f = "cerchio";
	let chiave: string;
	if (tipo === "anello") chiave = `anello|${(scelta("Forma anello") || "quadrifoglio").toLowerCase()}|`;
	else {
		const m = tipo === "collana" || tipo === "bracciale" ? (scelta("Misura") || "piccola").toLowerCase() : "";
		chiave = `${tipo}|${f}|${m}`;
	}
	const pts = forme[chiave];
	if (!pts || !pts.length) return null;
	let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
	for (const [x, y] of pts) {
		if (x < x0) x0 = x;
		if (x > x1) x1 = x;
		if (y < y0) y0 = y;
		if (y > y1) y1 = y;
	}
	return {tipo, larg: x1 - x0, alt: y1 - y0};
}

/** asse "verticale" del gioiello: Z se il modello e' in piedi (dal 28 set 2026), Y se e' sdraiato (modelli vecchi) */
export function asseVerticale(): 1 | 2 {
	try {
		const bb = sceneTree.root.boundingBox;
		if (!bb || bb.isEmpty()) return 2;
		// in piedi: il fronte della piastrina sta a y ~ -1.4 e tutto il resto dietro (y > 0);
		// sdraiato (modelli fino alla v23): la piastrina occupa y da -10 in giu'
		return bb.min[1] > -3 ? 2 : 1;
	} catch {
		return 2;
	}
}

/** riquadro della piastrina dentro la scena: sta in BASSO (catena, gancio e perno stanno sopra), centrata in X */
function riquadroPiastrina(): Box | null {
	try {
		const bb = sceneTree.root.boundingBox;
		if (!bb || bb.isEmpty()) return null;
		const v = asseVerticale();
		const largScena = bb.max[0] - bb.min[0];
		const altScena = bb.max[v] - bb.min[v];
		if (!(largScena > 0) || !(altScena > 0)) return null;
		const mis = misuraPiastrina();
		if (mis && mis.tipo === "anello") return null; // anello: si inquadra tutto (la scritta e' dentro il gambo)
		const cx = (bb.min[0] + bb.max[0]) / 2;
		let altP: number, largP: number;
		if (mis) {
			altP = Math.min(altScena, mis.alt + 1);
			// orecchini in coppia: due piastrine affiancate -> tutta la larghezza della scena
			largP = mis.tipo === "lobo" || mis.tipo === "pendente" ? largScena : Math.min(largScena, mis.larg + 1);
		} else {
			altP = Math.min(altScena, Math.max(altScena * 0.4, 12));
			largP = largScena;
		}
		const min = vec3.fromValues(cx - largP / 2, bb.min[1], bb.min[2]);
		const max = vec3.fromValues(cx + largP / 2, bb.max[1], bb.max[2]);
		max[v] = bb.min[v] + altP;
		return new Box(min, max);
	} catch {
		return null;
	}
}

type CameraCompleta = Camera & {
	position: Float32Array;
	target: Float32Array;
	fov?: number;
	set?: (position: Float32Array, target: Float32Array, options?: {duration?: number}) => Promise<boolean>;
};
/** quanto scende il bersaglio (in altezze visibili): 0.15 = il gioiello sta al centro della parte SOPRA il riquadro prezzo */
// (28 set sera) 0,02: con la colonna 3D piu' alta (padding 10vh) e la piastrina piu' grande, con -0,06 la
// piastrina della collana finiva SOPRA il riquadro prezzo; con 0,02 sta al centro dello spazio libero
const SPOSTA_GIU = 0.02;

/**
 * Inquadratura DETERMINISTICA: zoomTo(box) del visore inquadra la sfera del riquadro senza guardare la forma
 * del visore (nella colonna stretta del totem veniva o tagliato o lontano). Qui: prima zoomTo(box, durata 0)
 * per fissare bersaglio e direzione, poi la DISTANZA viene calcolata dal campo visivo e dal rapporto
 * larghezza/altezza del visore, cosi' che il riquadro riempia il visore per "riempimento" (0.8 = 80%).
 */
/* Il dito del cliente sul 3D (28 set sera): mentre il dito e' giu', e per qualche secondo dopo, l'inquadratura
 * automatica (che riparte dopo ogni calcolo) NON deve scattare, altrimenti la rotazione "torna indietro" da
 * sola e sembra bloccata. Il pulsante CENTRA inquadra sempre (chiama inquadra con forza=true). */
let ditoGiu = false;
let ultimoTocco = 0;
const PAUSA_DOPO_TOCCO_MS = 8000;
/* (16 ott 2026, video di Davide: «quando cambio la scritta l'oggetto resta in posizione una frazione di secondo e poi
 * si rimette in posizione di partenza: deve rimanere nella posizione messa dal cliente») in modalita' dedica, dopo
 * ogni lettera il gioiello veniva ri-inquadrato sul retro appena passati gli 8 secondi dall'ultimo tocco. Ora, se il
 * cliente ha girato/zoomato il gioiello, le inquadrature AUTOMATICHE della dedica non scattano piu' finche' non arriva
 * una scelta vera (forma, modello, misura...: zoomDedicaOn con riparti) o il pulsante CENTRA GIOIELLO. */
let clienteHaMosso = false;
/* (16 ott 2026, Davide: «la posizione e la prospettiva di default degli anelli, quando vengono scelti, devono essere
 * uguali a quelle delle figure sui bottoni degli anelli»; anche per Chora) dal modello eidos-sagoma-v21 / eidos-forme-v41
 * l'anello arriva SDRAIATO (piastra in alto, dito lungo Y), come nelle foto dei pulsanti (sagoma/foto_pulsanti_sagoma.py:
 * vista dall'alto dopo la rotazione PROSP). DIR_ANELLO = da dove guarda la camera di quelle foto (dal bersaglio verso la
 * camera, coordinate del modello): calcolata in Rhino come PROSP^-1 · (0, 0, 1). Con "su" = Z il visore rifa' la stessa
 * immagine. La direzione "di base" vale dopo ogni scelta di modello/forma finche' il cliente non gira il gioiello. */
const DIR_ANELLO = [-0.3755, -0.6874, 0.6216];
const DIR_FRONTE = [0, -0.9928, 0.1191]; // la camera iniziale del modello (0, -100, 12)
let dirBase: number[] | null = null;
let eraAnello = false;
/** il cliente ha scelto un modello o una forma: anello -> vista delle foto; uscendo dall'anello -> di nuovo di fronte */
export function sceltaGioiello(anello: boolean) {
	if (anello) dirBase = DIR_ANELLO;
	else if (eraAnello) dirBase = DIR_FRONTE;
	eraAnello = anello;
}
function eAnello(): boolean {
	return MODELLI[scelta("Modello")] === "anello";
}
try {
	document.addEventListener(
		"pointerdown",
		(e) => {
			const t = e.target as HTMLElement | null;
			if (t?.tagName === "CANVAS" && !t.classList.contains("mapboxgl-canvas")) {
				ditoGiu = true;
				ultimoTocco = Date.now();
				clienteHaMosso = true;
				dirBase = null; // da qui la direzione di vista la decide il cliente
			}
		},
		{passive: true},
	);
	const su = () => {
		if (ditoGiu) ultimoTocco = Date.now();
		ditoGiu = false;
	};
	document.addEventListener("pointerup", su, {passive: true});
	document.addEventListener("pointercancel", su, {passive: true});
	// (6 ott) anche la rotella del mouse sul 3D e' un "tocco": zoomando, la centratura del computer non deve scattare
	document.addEventListener(
		"wheel",
		(e) => {
			const t = e.target as HTMLElement | null;
			if (t?.tagName === "CANVAS" && !t.classList.contains("mapboxgl-canvas")) {
				ultimoTocco = Date.now();
				clienteHaMosso = true;
			}
		},
		{passive: true, capture: true},
	);
} catch {
	/* ignora */
}
function ditoRecente(): boolean {
	return ditoGiu || Date.now() - ultimoTocco < PAUSA_DOPO_TOCCO_MS;
}

/**
 * FASCIA LIBERA del visore (5 ott 2026 sera, telefono di Davide: "logo e gioiello si sovrappongono"):
 * sopra il 3D sta il LOGO, sotto il riquadro PREZZO/carrello; il gioiello deve stare nella fascia fra i due.
 * Restituisce, in frazioni dell'altezza del visore, quanto e' coperto sopra (logo) e sotto (prezzo).
 */
function bandaLibera(): {sopra: number; sotto: number} {
	const nulla = {sopra: 0, sotto: 0};
	try {
		const c = canvasVisore();
		if (!c) return nulla;
		const rc = rettVisore(c);
		if (rc.height <= 0) return nulla;
		let sopra = 0;
		let sotto = 0;
		const prezzo = document.querySelector(".mantine-Group-root:has(> .mantine-Paper-root [class*=\"markdownNormalize\"])");
		if (prezzo) {
			const rb = prezzo.getBoundingClientRect();
			// parte del visore (dal bordo alto del riquadro al fondo del visore) coperta dal riquadro
			if (rb.height > 0) sotto = Math.max(0, rc.bottom - Math.max(rb.top, rc.top)) / rc.height;
		}
		const logo = document.querySelector('[class*="_appShellGridAreaMain_"] .mantine-Image-root, .mantine-Stack-root > .mantine-Image-root');
		if (logo) {
			const rl = logo.getBoundingClientRect();
			// parte del visore (dal bordo alto del visore al fondo del logo) coperta dal logo
			if (rl.height > 0 && rl.bottom > rc.top && rl.top < rc.bottom) sopra = Math.max(0, Math.min(rl.bottom, rc.bottom) - rc.top) / rc.height;
		}
		// sanita': la fascia libera resta almeno il 35% del visore
		if (sopra + sotto > 0.65) {
			const k = 0.65 / (sopra + sotto);
			sopra *= k;
			sotto *= k;
		}
		return {sopra, sotto};
	} catch {
		return nulla;
	}
}

/** di quanto abbassare il bersaglio (in altezze visibili) perche' il gioiello stia al CENTRO della fascia libera
 *  (minimo SPOSTA_GIU: un filo piu' in alto del centro anche senza nulla di coperto) */
function frazioneGiu(banda = bandaLibera()): number {
	// centro della fascia libera, in frazione dall'alto: se e' sopra la meta', il bersaglio scende e il gioiello sale
	const centro = (banda.sopra + (1 - banda.sotto)) / 2;
	return Math.max(SPOSTA_GIU, Math.min(0.45, 0.5 - centro));
}

/** il canvas del visore 3D (non quello della mappa Mapbox): il piu' grande sullo schermo */
function canvasVisore(): HTMLCanvasElement | null {
	let migliore: HTMLCanvasElement | null = null;
	let area = 0;
	document.querySelectorAll("canvas").forEach((c) => {
		if (c.classList.contains("mapboxgl-canvas")) return;
		const a = c.clientWidth * c.clientHeight;
		if (a > area) {
			area = a;
			migliore = c;
		}
	});
	return migliore;
}

/** rettangolo (px CSS) dell'area in cui la camera disegna: il CANVAS quando ha una misura plausibile, altrimenti il
 *  suo contenitore. (28 set) dopo il caricamento il canvas resta per un po' a 300x150: allora vale il contenitore.
 *  (6 ott) all'avvio, invece, il contenitore puo' risultare piu' ALTO della finestra (1272 px su 768) mentre il canvas
 *  e' giusto: misurando il contenitore il riquadro prezzo sembrava coprire il 57% del visore e la piastrina non
 *  veniva centrata. */
function rettVisore(c: HTMLCanvasElement): DOMRect {
	const rc = c.getBoundingClientRect();
	const genitore = c.parentElement;
	if (!genitore) return rc;
	const rp = genitore.getBoundingClientRect();
	const plausibile = rc.width >= 200 && rc.height >= 200 && Math.abs(rc.width - rp.width) <= 2;
	return plausibile ? rc : rp;
}

/** larghezza/altezza (px CSS) dell'area del visore 3D (vedi rettVisore) */
function dimensioniVisore(): {w: number; h: number} {
	const c = canvasVisore();
	if (!c) return {w: 0, h: 0};
	const r = rettVisore(c);
	if (r.width > 50 && r.height > 50) return {w: Math.round(r.width), h: Math.round(r.height)};
	if (c.clientWidth > 0 && c.clientHeight > 0) return {w: c.clientWidth, h: c.clientHeight};
	return {w: 0, h: 0};
}

/** diario delle inquadrature (per capire dal browser cosa e' successo): window.__eidosDedica.diario() */
const diario: string[] = [];
function nota(s: string) {
	diario.push(new Date().toISOString().slice(11, 23) + " " + s);
	if (diario.length > 40) diario.shift();
}

let generazioneInquadra = 0;

function inquadra(box: Box | null, riempimento: number, forza = false) {
	if (!forza && ditoRecente()) {
		nota("inquadra saltata: dito recente");
		return;
	}
	try {
		const sb = sceneTree.root.boundingBox;
		nota(
			"inquadra box=" + (box ? "si" : "scena") + " riemp=" + riempimento + " forza=" + forza + " scenaX=" + (sb && !sb.isEmpty() ? (sb.max[0] - sb.min[0]).toFixed(1) : "vuota"),
		);
	} catch {
		nota("inquadra box=" + (box ? "si" : "scena"));
	}
	// ⚠️ (28 set notte) NON document.querySelector("canvas"): il PRIMO canvas della pagina puo' essere quello
	// della MAPPA (Mapbox, quasi quadrato) -> proporzione sbagliata -> distanza sbagliata (gioiello tagliato o
	// troppo in basso). Si prende il canvas del visore 3D: il piu' grande fra quelli che non sono della mappa.
	const canvas = canvasVisore();
	// (28 set notte) la MISURA si prende dal CONTENITORE del canvas, non dal canvas: dopo il caricamento il canvas
	// del visore resta per un po' alla misura predefinita 300x150 (proporzione 2 invece di 0,38 sul totem) e la
	// distanza veniva calcolata per un visore largo e basso: gioiello tagliato o troppo in basso
	const {w: lw, h: lh} = dimensioniVisore();
	const aspect = lh > 0 ? lw / lh : 1;
	for (const cam of camere()) {
		try {
			const c = cam as CameraCompleta;
			// niente zoomTo del visore (la sua animazione rendeva il risultato casuale): bersaglio = centro del
			// riquadro, direzione = quella attuale della camera, distanza calcolata qui sotto
			let bx: {min: ArrayLike<number>; max: ArrayLike<number>} | null = box;
			if (!bx) {
				const bb = sceneTree.root.boundingBox;
				if (!bb || bb.isEmpty()) continue;
				bx = bb;
			}
			let dx = bx.max[0] - bx.min[0];
			let dv = Math.max(bx.max[1] - bx.min[1], bx.max[2] - bx.min[2]);
			if (eAnello()) {
				// (16 ott 2026) l'anello si guarda di sbieco (DIR_ANELLO): visto cosi' occupa circa l'85% della diagonale
				// del suo riquadro, non la sola larghezza/altezza (con la dedica la piastra usciva dal bordo in alto)
				const diag = Math.hypot(bx.max[0] - bx.min[0], bx.max[1] - bx.min[1], bx.max[2] - bx.min[2]);
				// (26 ott 2026) sul telefono un po' di margine in piu': nel 3D ridotto mentre si scrive l'incisione la piastra
					// dell'anello toccava il bordo in alto
					dx = dv = (schermoTelefono() && !document.body.classList.contains("eidos-totem") ? 1.0 : 0.85) * diag;
			}
			const vfov = ((c.fov ?? 60) * Math.PI) / 180;
			const t = [(bx.min[0] + bx.max[0]) / 2, (bx.min[1] + bx.max[1]) / 2, (bx.min[2] + bx.max[2]) / 2];
			// direzione di vista FISSA (non quella lasciata dal cliente): da davanti, un po' dall'alto.
			// Se la direzione seguisse l'ultimo trascinamento, la camera poteva finire "a picco" sul gioiello
			// e da li' i gesti sembravano bloccati (28 set 2026).
			// (16 ott 2026) anello: la vista delle foto dei pulsanti
			const dir = eAnello() ? DIR_ANELLO : asseVerticale() === 2 ? [0, -1, 0.12] : [0, -0.62, 0.78];
			const len = Math.hypot(dir[0], dir[1], dir[2]) || 1;
			// distanza perche' la larghezza stia nel visore E perche' l'altezza stia nella FASCIA LIBERA del visore
			// (5 ott sera: fra il logo in alto e il riquadro prezzo in basso; sul telefono la fascia e' meta' del 3D)
			const banda = bandaLibera();
			// (26 ott 2026, video di Davide: l'anello girato col dito «scappava» dietro il riquadro prezzo) TELEFONO:
				// il bersaglio della camera resta il CENTRO del gioiello (la rotazione col dito gira attorno a lui e il
				// gioiello resta al suo posto) e il gioiello sta nella fascia SIMMETRICA attorno al centro del 3D che il
				// riquadro prezzo non copre. Prima il bersaglio scendeva sotto il gioiello: girandolo, il gioiello si spostava.
				const telefono = schermoTelefono() && !document.body.classList.contains("eidos-totem");
				const libera = telefono
					? Math.max(0.35, 1 - 2 * Math.max(banda.sopra, banda.sotto))
					: Math.max(0.35, 1 - banda.sopra - banda.sotto);
			const dLarg = dx / riempimento / (2 * Math.tan(vfov / 2) * aspect);
			const dAlt = dv / (riempimento * libera) / (2 * Math.tan(vfov / 2));
			const d = Math.max(dLarg, dAlt, 20);
			nota(
				`  visore=${lw}x${lh} (canvas ${canvas ? canvas.clientWidth + "x" + canvas.clientHeight : "nessuno"}) aspect=${aspect.toFixed(3)} fov=${c.fov ?? "?"} dx=${dx.toFixed(1)} dv=${dv.toFixed(1)} fascia=${(libera * 100).toFixed(0)}% (sopra ${(banda.sopra * 100).toFixed(0)}% sotto ${(banda.sotto * 100).toFixed(0)}%) d=${d.toFixed(1)}`,
			);
			// il riquadro prezzo/carrello copre il fondo del visore: il bersaglio scende un po' lungo l'asse
			// verticale dello schermo, cosi' il gioiello sta nella parte alta libera (sopra il riquadro)
			const altezzaVisibile = 2 * d * Math.tan(vfov / 2);
			const u = [dir[0] / len, dir[1] / len, dir[2] / len];
			// "su" dello schermo = Z del mondo tolta la componente lungo la direzione di vista
			const zz = [0 - u[0] * u[2], 0 - u[1] * u[2], 1 - u[2] * u[2]];
			const lz = Math.hypot(zz[0], zz[1], zz[2]) || 1;
			const su = [zz[0] / lz, zz[1] / lz, zz[2] / lz];
			// (3 ott 2026) quanto scendere dipende da quanto il riquadro prezzo/carrello copre il fondo del visore:
			// il gioiello va al centro della parte LIBERA sopra il riquadro (telefono ~1/4 del 3D, totem poco)
			const giu = telefono ? 0 : frazioneGiu(banda) * altezzaVisibile;
			const t2 = new Float32Array([t[0] - su[0] * giu, t[1] - su[1] * giu, t[2] - su[2] * giu]);
			const p2 = new Float32Array([t2[0] + u[0] * d, t2[1] + u[1] * d, t2[2] + u[2] * d]);
			const gen = ++generazioneInquadra;
			const applica = () => {
				// (5 ott) le ripetizioni a 400/1200 ms NON devono scattare se nel frattempo il cliente ha messo il
				// dito sul 3D, o se e' partita un'inquadratura piu' nuova (altrimenti "la rotazione torna indietro")
				if (gen !== generazioneInquadra) return;
				if (!forza && ditoRecente()) return;
				try {
					// (28 set notte) camera.set(...) con durata 0: e' il modo ufficiale del visore e FERMA la sua
					// animazione in corso. Scrivere position/target direttamente veniva sovrascritto dall'animazione
					// del visore (all'avvio e dopo i calcoli): i numeri erano giusti ma la camera restava la sua.
					if (typeof c.set === "function") {
						void c.set(new Float32Array(p2), new Float32Array(t2), {duration: 0});
						const leggi = () => Array.from(c.position).map((x) => x.toFixed(1)).join(",") + " -> " + Array.from(c.target).map((x) => x.toFixed(1)).join(",");
						nota("  set " + Array.from(p2).map((x) => x.toFixed(1)).join(",") + " | cam ora " + leggi());
						window.setTimeout(() => nota("  +600ms cam " + leggi()), 600);
					} else {
						c.target = t2;
						c.position = p2;
					}
				} catch {
					/* ignora */
				}
			};
			applica();
			// se il visore stava ancora animando il suo zoomTo, la nostra posizione va ribadita (anche piu' tardi:
			// l'animazione del visore puo' durare piu' di 400 ms e una volta gli orecchini sono rimasti tagliati)
			window.setTimeout(applica, 400);
			window.setTimeout(applica, 1200);
		} catch {
			/* ignora */
		}
	}
}

function inquadraPiastrina() {
	if (clienteHaMosso) {
		nota("dedica: inquadratura saltata, il cliente ha girato il gioiello");
		return;
	}
	void inquadra(riquadroPiastrina(), 0.85);
}

/** entra in modalita' dedica: zoom sulla piastrina, e lo rifa' dopo ogni aggiornamento del modello.
 *  riparti = true (una scelta vera: forma, modello, misura...): si inquadra anche se il cliente aveva girato il gioiello;
 *  false (solo il testo, il carattere o l'allineamento cambiati): la posizione data dal cliente resta */
export function zoomDedicaOn(riparti = true) {
	if (riparti) clienteHaMosso = false;
	if (!attivo) {
		attivo = true;
		const ridedica = () => {
			if (!attivo) return;
			if (timer !== undefined) window.clearTimeout(timer);
			// il visore fa il suo "camera adjust" subito dopo l'evento: noi passiamo dopo di lui
			timer = window.setTimeout(inquadraPiastrina, 900);
		};
		try {
			tokenAscolto = addListener(EVENTTYPE.SCENE.SCENE_BOUNDING_BOX_CHANGE, ridedica);
			tokenAscolto2 = addListener(EVENTTYPE.SESSION.SESSION_CUSTOMIZED, ridedica);
		} catch {
			tokenAscolto = null;
		}
	}
	if (timer !== undefined) window.clearTimeout(timer);
	timer = window.setTimeout(inquadraPiastrina, 200);
}

/** il cliente ha cambiato il TESTO (o carattere/allineamento): la prima volta il gioiello si gira sulla dedica, poi resta
 *  dove l'ha messo il cliente */
export function zoomDedicaTesto() {
	zoomDedicaOn(!attivo);
}

/** pulsante CENTRA GIOIELLO (16 ott 2026: «non funziona»): in modalita' dedica inquadraTotem non faceva nulla (usciva
 *  subito perche' la dedica era attiva). Ora centra SEMPRE: sulla piastrina con la dedica, altrimenti come all'avvio. */
export function centraGioiello() {
	clienteHaMosso = false;
	nota("CENTRA GIOIELLO: dedica=" + attivo + " totem=" + totemAttivo + " pc=" + pcAttivo);
	if (attivo) {
		void inquadra(riquadroPiastrina(), 0.85, true);
		return;
	}
	if (totemAttivo) {
		inquadraTotem(true);
		return;
	}
	if (pcAttivo) {
		inquadraPC(true);
		return;
	}
	for (const cam of camere()) {
		try {
			cam.zoomTo?.();
		} catch {
			/* ignora */
		}
	}
}

/** esce dalla modalita' dedica: si torna a vedere tutto il gioiello con il fattore dato */
export function zoomDedicaOff(fattore: number) {
	if (!attivo) return;
	attivo = false;
	clienteHaMosso = false;
	if (timer !== undefined) window.clearTimeout(timer);
	for (const tk of [tokenAscolto, tokenAscolto2]) {
		if (!tk) continue;
		try {
			removeListener(tk);
		} catch {
			/* ignora */
		}
	}
	tokenAscolto = null;
	tokenAscolto2 = null;
	if (totemAttivo) {
		window.setTimeout(inquadraTotem, 50);
		return;
	}
	if (pcAttivo) {
		// computer (6 ott): il fattore per tipo di gioiello lo tiene EidosCamera; qui solo la nostra inquadratura
		window.setTimeout(() => inquadraPC(true), 50);
		return;
	}
	for (const cam of camere()) {
		try {
			cam.zoomToFactor = fattore;
			cam.zoomTo?.();
		} catch {
			/* ignora */
		}
	}
}

export function zoomDedicaAttivo(): boolean {
	return attivo;
}

/* ---------------------------------------------------------------- inquadratura del TOTEM (27 set 2026)
 * Davide: "avvicina di piu' gli oggetti, il bracciale e' veramente lontano". Sul totem la colonna del 3D e'
 * stretta e il visore inquadra TUTTO il gioiello con margine: il bracciale (catena da 16-19 cm) diventa minuscolo.
 * Qui: fattore piu' stretto per tutti, e per il bracciale un riquadro = piastrina + un pezzo di catena per lato.
 */
const CATENA_BRACCIALE = 0.9; // quanta catena mostrare per lato, in larghezze di piastrina (28 set sera: 0,4 era "troppo zoom")
let totemAttivo = false;
let tokenTotem: string | null = null;
let tokenTotem2: string | null = null;
let timerTotem: number | undefined;
const timersTotem: number[] = [];
let rifaiTotem: (() => void) | null = null;

/** quanta catena mostrare SOPRA la piastrina (collana, pendente), in altezze di piastrina */
const CATENA_COLLANA = 0.35;

function riquadroTotem(): Box | null {
	try {
		const bb = sceneTree.root.boundingBox;
		if (!bb || bb.isEmpty()) return null;
		const mis = misuraPiastrina();
		if (!mis) return null;
		const cx = (bb.min[0] + bb.max[0]) / 2;
		if (mis.tipo === "bracciale") {
			// piastrina + un tratto di catena per lato
			const mezza = Math.min((bb.max[0] - bb.min[0]) / 2, mis.larg / 2 + mis.larg * CATENA_BRACCIALE);
			return new Box(
				vec3.fromValues(cx - mezza, bb.min[1], bb.min[2]),
				vec3.fromValues(cx + mezza, bb.max[1], bb.max[2]),
			);
		}
		// (26 ott 2026, foto di Davide: «gli orecchini si vedono male») orecchini PENDENTI: tutto il gioiello, ganci
		// compresi (prima piastrina + un pezzetto di gancio: i ganci uscivano in alto e sotto restava spazio vuoto)
		if (mis.tipo === "pendente") return null;
		if (mis.tipo === "collana") {
			// piastrina + un pezzo di catena/gancio sopra (Davide, 28 set: "la catena basta che si veda un po'")
			const v = asseVerticale();
			const alt = Math.min(bb.max[v] - bb.min[v], mis.alt + mis.alt * CATENA_COLLANA + 1);
			// collana: piastrina piu' grande (era larg*0,75: la piastrina occupava solo 2/3 della colonna)
			const mezza = Math.min((bb.max[0] - bb.min[0]) / 2, mis.larg * 0.66);
			const min = vec3.fromValues(cx - mezza, bb.min[1], bb.min[2]);
			const max = vec3.fromValues(cx + mezza, bb.max[1], bb.max[2]);
			max[v] = bb.min[v] + alt;
			return new Box(min, max);
		}
		return null; // anello, orecchino a lobo: tutto il gioiello
	} catch {
		return null;
	}
}

/** inquadra il gioiello come vuole il totem (se non e' attiva la modalita' dedica) */
export function inquadraTotem(forza = false) {
	if (attivo) return;
	const rq = riquadroTotem();
	// tutto il gioiello (lobo, anello): un po' piu' di margine, altrimenti gli orecchini toccavano i bordi
	void inquadra(rq, rq ? 0.9 : 0.84, forza);
}

/** (5 ott sera) sul telefono il 3D cambia altezza quando si apre/chiude una tendina: si ri-inquadra */
let osservatoreVisore: ResizeObserver | null = null;
let timerRidimensiona: number | undefined;
function osservaMisuraVisore() {
	if (osservatoreVisore || typeof ResizeObserver === "undefined") return;
	const attacca = () => {
		const c = canvasVisore();
		const el = c?.parentElement;
		if (!el) return false;
		osservatoreVisore = new ResizeObserver(() => {
			if (timerRidimensiona !== undefined) window.clearTimeout(timerRidimensiona);
			// la transizione CSS dura 0,35 s: si inquadra a transizione finita (e una seconda volta per sicurezza)
			timerRidimensiona = window.setTimeout(() => {
				if (totemAttivo) {
					inquadraTotem(true);
					window.setTimeout(() => inquadraTotem(true), 500);
				} else if (pcAttivo) {
					// computer (6 ott): finestra ridimensionata -> il riquadro prezzo copre una parte diversa del visore
					alzaSopraPrezzo();
				}
			}, 450);
		});
		osservatoreVisore.observe(el);
		return true;
	};
	if (!attacca()) {
		const inizio = Date.now();
		const t = window.setInterval(() => {
			if (attacca() || Date.now() - inizio > 20000) window.clearInterval(t);
		}, 500);
	}
}

/** da chiamare una volta sul totem: dopo ogni aggiornamento del modello si rifa' l'inquadratura */
export function avviaZoomTotem() {
	if (totemAttivo) return;
	totemAttivo = true;
	osservaMisuraVisore();
	// (28 set notte) non UNA inquadratura a 900 ms ma TRE (0,9 / 2 / 3,5 s), ognuna ricalcolata con la scena del
	// momento: la geometria nuova arriva nella scena anche 1-2 s dopo "SESSION_CUSTOMIZED", e la prima
	// inquadratura vedeva ancora il gioiello VECCHIO (orecchini a lobo tagliati con la distanza della collana)
	const rifai = () => {
		if (timerTotem !== undefined) window.clearTimeout(timerTotem);
		for (const t of timersTotem) window.clearTimeout(t);
		timersTotem.length = 0;
		// fino a 10 s: la geometria nuova (e all'avvio l'ambiente HDR + la prima animazione del visore) possono
		// arrivare anche molti secondi dopo l'evento; se il cliente tocca il 3D le inquadrature si saltano da sole
		for (const ms of [900, 2000, 3500, 5000, 7000, 10000]) timersTotem.push(window.setTimeout(() => inquadraTotem(), ms));
	};
	rifaiTotem = rifai;
	try {
		tokenTotem = addListener(EVENTTYPE.SCENE.SCENE_BOUNDING_BOX_CHANGE, rifai);
		// anche quando cambia solo la mappa dentro la piastrina (bbox uguale): il visore ri-inquadra a modo suo
		tokenTotem2 = addListener(EVENTTYPE.SESSION.SESSION_CUSTOMIZED, rifai);
	} catch {
		tokenTotem = null;
	}
	// PRIMA inquadratura (28 set sera): all'avvio la scena e i contorni (forme.json) arrivano DOPO questa chiamata,
	// e l'evento "bounding box cambiato" del primo caricamento e' gia' passato: un timer fisso di 300 ms trovava
	// la scena vuota e il gioiello restava com'era inquadrato dal visore. Ora si aspetta che scena e contorni
	// ci siano (controllo ogni 400 ms, al massimo 20 s) e poi si inquadra.
	const inizio = Date.now();
	const attesa = window.setInterval(() => {
		let pronto = false;
		try {
			const bb = sceneTree.root.boundingBox;
			const dim = dimensioniVisore();
			pronto = !!bb && !bb.isEmpty() && !!forme && dim.w > 100 && dim.h > 100;
		} catch {
			pronto = false;
		}
		if (pronto || Date.now() - inizio > 20000 || !totemAttivo) {
			window.clearInterval(attesa);
			if (totemAttivo) rifaiTotem?.(); // tre inquadrature: la prima animazione del visore puo' ancora sovrascriverci
		}
	}, 400);
}

export function fermaZoomTotem() {
	totemAttivo = false;
	if (timerTotem !== undefined) window.clearTimeout(timerTotem);
	for (const t of timersTotem) window.clearTimeout(t);
	timersTotem.length = 0;
	rifaiTotem = null;
	for (const tk of [tokenTotem, tokenTotem2]) {
		if (!tk) continue;
		try {
			removeListener(tk);
		} catch {
			/* ignora */
		}
	}
	tokenTotem = null;
	tokenTotem2 = null;
}


/* ---------------------------------------------------------------- COMPUTER: PIASTRINA al centro e SOPRA il riquadro prezzo (6 ott 2026)
 * Davide (6 ott pomeriggio): "quando si sceglie la collana va messa al centro della finestra: il centro e' quello
 * dell'ingombro del CIONDOLO, non di ciondolo e catenina"; e poi: "cambiando segnaposti, forme e modelli il
 * configuratore rallenta vistosamente" -> con il "Camera adjust" del visore + la nostra correzione dopo, ogni scelta
 * faceva DUE movimenti di camera (~1,5 s di animazioni dopo il calcolo). Ora sul computer il visore NON inquadra
 * piu' da solo (autoAdjust e initialAutoAdjust spenti in EidosTotem): la sua inquadratura la calcoliamo noi con
 * camera.calculateZoomTo() (la stessa formula del visore, con il suo zoomToFactor per tipo di gioiello) e la
 * spostiamo SUBITO sul centro della piastrina (collana/bracciale/pendente: sta in basso nella scena, alta quanto
 * dice forme.json; la catena resta sopra, tagliata dal bordo). Se il bordo basso cade dietro il riquadro prezzo si
 * sale quanto basta; anello e lobo (senza catena) non si tagliano: se non ci stanno nella fascia libera fra logo e
 * prezzo la camera si allontana. UNA sola animazione (450 ms), deterministica: se la camera e' gia' li' non si
 * tocca nulla. Si proietta CON la prospettiva (la piastrina dell'anello sta 10 mm davanti al centro della scena).
 * Quando: dopo ogni calcolo (SESSION_CUSTOMIZED) e ogni cambio del riquadro della scena, con piu' passaggi perche'
 * la geometria nuova puo' arrivare anche secondi dopo; al ridimensionamento; dopo uno zoom del visore chiesto da
 * altri (pulsante della barra, CAMERA_END). Mai in modalita' dedica, sul totem/telefono (li' inquadraTotem), mentre
 * il cliente preme sul 3D, e (salvo dopo un calcolo) nei secondi dopo un suo tocco o una rotellata. */
const MARGINE_PREZZO = 0.02; // spazio fra il gioiello e il riquadro, in altezze del visore
const DURATA_PC_MS = 450;
const CON_CATENA = new Set(["collana", "bracciale", "pendente"]);
let pcAttivo = false;
let tokenPC: string | null = null;
let tokenPC2: string | null = null;
let tokenPC3: string | null = null;
let tokenPC4: string | null = null;
const timersPC: number[] = [];
let movimentiCamera = 0; // CAMERA_START contati (per le prove: quante animazioni per scelta)
let tUltimoCalcolo = 0; // quando e' arrivato l'ultimo calcolo / cambio di scena
let setDopoCalcolo = false; // true se dopo l'ultimo calcolo abbiamo gia' mosso la camera
let ultimoSetPC: {p: number[]; t: number[]; quando: number} | null = null;

type CameraPC = CameraCompleta & {
	calculateZoomTo?: (box?: unknown) => {position: ArrayLike<number>; target: ArrayLike<number>};
};

function vicini(a: ArrayLike<number>, b: ArrayLike<number>, tolleranza = 0.1): boolean {
	return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) < tolleranza;
}

/** inquadratura del computer: formula del visore + piastrina al centro + sopra il prezzo, in una sola animazione */
export function inquadraPC(forza = false, ignoraFascia = false) {
	// dito/mouse premuto sul 3D: si aspetta (ma un "premuto" piu' vecchio di 15 s e' un pointerup perso fuori
	// dalla finestra: si ignora)
	const premuto = ditoGiu && Date.now() - ultimoTocco < 15000;
	if (attivo || totemAttivo || premuto) return;
	if (!forza && ditoRecente()) {
		nota("inquadraPC saltata: dito recente");
		return;
	}
	if (dimensioniVisore().h < 250) {
		nota("inquadraPC saltata: visore troppo piccolo");
		return;
	}
	let bb: {min: ArrayLike<number>; max: ArrayLike<number>; isEmpty: () => boolean};
	try {
		bb = sceneTree.root.boundingBox;
		if (!bb || bb.isEmpty()) return;
	} catch {
		return;
	}
	let banda = bandaLibera();
	if (banda.sotto > 0.45) {
		if (!ignoraFascia) {
			nota("inquadraPC saltata: fascia sospetta (riquadro prezzo " + (banda.sotto * 100).toFixed(0) + "% del visore)");
			return;
		}
		nota("inquadraPC: fascia sospetta ignorata (ultimo passaggio)");
		banda = {sopra: 0, sotto: 0};
	}
	const v = asseVerticale();
	const tipo = MODELLI[scelta("Modello")] || "collana";
	const mis = misuraPiastrina();
	for (const cam of camere()) {
		try {
			const c = cam as CameraPC;
			// 1) l'inquadratura che farebbe il visore (stessa direzione di vista attuale, suo zoomToFactor)
			let p: number[];
			let t: number[];
			if (typeof c.calculateZoomTo === "function") {
				const r = c.calculateZoomTo();
				p = Array.from(r.position);
				t = Array.from(r.target);
			} else {
				p = Array.from(c.position);
				t = Array.from(c.target);
			}
			if (![...p, ...t].every((x) => Number.isFinite(x))) continue;
			let d = Math.hypot(p[0] - t[0], p[1] - t[1], p[2] - t[2]);
			if (!(d > 0)) continue;
			const tan = Math.tan((((c.fov ?? 60) * Math.PI) / 180) / 2);
			let u = [(p[0] - t[0]) / d, (p[1] - t[1]) / d, (p[2] - t[2]) / d]; // dal bersaglio verso la camera
			// (16 ott 2026) dopo la scelta di un anello (o tornando agli altri gioielli) la direzione e' quella "di base"
			if (dirBase) {
				const lb = Math.hypot(dirBase[0], dirBase[1], dirBase[2]) || 1;
				u = [dirBase[0] / lb, dirBase[1] / lb, dirBase[2] / lb];
			}
			// "su" dello schermo = Z del mondo tolta la componente lungo la direzione di vista
			const zz = [0 - u[0] * u[2], 0 - u[1] * u[2], 1 - u[2] * u[2]];
			const lz = Math.hypot(zz[0], zz[1], zz[2]) || 1;
			const su = [zz[0] / lz, zz[1] / lz, zz[2] / lz];
			const lungoSu = (q: number[]) => (q[0] - t[0]) * su[0] + (q[1] - t[1]) * su[1] + (q[2] - t[2]) * su[2];
			const lungoU = (q: number[]) => (q[0] - t[0]) * u[0] + (q[1] - t[1]) * u[1] + (q[2] - t[2]) * u[2]; // + = piu' vicino
			const spigoli: number[][] = [];
			for (const x of [bb.min[0], bb.max[0]]) for (const y of [bb.min[1], bb.max[1]]) for (const zq of [bb.min[2], bb.max[2]]) spigoli.push([x, y, zq]);
			// 2) centro della piastrina (collana/bracciale/pendente: in basso, alta mis.alt); anello/lobo: centro di tutto
			const centroP = [(bb.min[0] + bb.max[0]) / 2, (bb.min[1] + bb.max[1]) / 2, (bb.min[2] + bb.max[2]) / 2];
			if (CON_CATENA.has(tipo) && mis) centroP[v] = bb.min[v] + Math.min(bb.max[v] - bb.min[v], mis.alt) / 2;
			// spinta = di quanto SCENDE il bersaglio lungo il "su" dello schermo (il gioiello sale): portare il
			// bersaglio sul centro della piastrina la mette al centro del visore, a qualunque profondita' stia
			let spinta = -lungoSu(centroP);
			// frazione dall'alto del visore in cui cade un punto, CON la prospettiva (profondita' del punto)
			const frazione = (q: number[], dist: number) => {
				const prof = Math.max(1, dist - lungoU(q));
				return {fr: 0.5 - (lungoSu(q) + spinta) / (2 * prof * tan), prof};
			};
			const bordi = (dist: number) => {
				let basso = -Infinity, alto = Infinity, profBasso = dist;
				for (const q of spigoli) {
					const {fr, prof} = frazione(q, dist);
					if (fr > basso) {
						basso = fr;
						profBasso = prof;
					}
					if (fr < alto) alto = fr;
				}
				return {basso, alto, profBasso};
			};
			// 3) frazioni dell'altezza del visore, dall'alto: oltre limiteBasso il gioiello e' dietro il riquadro prezzo
			const limiteBasso = 1 - banda.sotto - MARGINE_PREZZO;
			const limiteAlto = banda.sopra + MARGINE_PREZZO;
			let b = bordi(d);
			if (!CON_CATENA.has(tipo) && b.basso > limiteBasso + 0.002) {
				// senza catena non si taglia nulla in alto: se il gioiello e' piu' alto della fascia libera, ci si allontana
				const fascia = Math.max(0.2, limiteBasso - limiteAlto);
				for (let i = 0; i < 3 && b.basso - b.alto > fascia; i++) {
					d *= (b.basso - b.alto) / fascia;
					b = bordi(d);
				}
			}
			if (b.basso > limiteBasso + 0.002) {
				// ...poi, se il bordo basso resta dietro il riquadro prezzo, si sale ancora quanto basta
				spinta += (b.basso - limiteBasso) * 2 * b.profBasso * tan;
				b = bordi(d);
			}
			const t2 = [t[0] - su[0] * spinta, t[1] - su[1] * spinta, t[2] - su[2] * spinta];
			const p2 = [t2[0] + u[0] * d, t2[1] + u[1] * d, t2[2] + u[2] * d];
			// 4) se la camera e' gia' li' (o ci sta andando con la nostra animazione), niente da fare
			if (vicini(c.position, p2) && vicini(c.target, t2)) continue;
			if (ultimoSetPC && Date.now() - ultimoSetPC.quando < DURATA_PC_MS + 300 && vicini(ultimoSetPC.p, p2) && vicini(ultimoSetPC.t, t2)) continue;
			ultimoSetPC = {p: p2, t: t2, quando: Date.now()};
			setDopoCalcolo = true;
			nota(`inquadraPC: bordo basso ${(b.basso * 100).toFixed(0)}% (limite ${(limiteBasso * 100).toFixed(0)}%) spinta ${spinta.toFixed(1)} d=${d.toFixed(1)} forza=${forza}`);
			if (typeof c.set === "function") void c.set(new Float32Array(p2), new Float32Array(t2), {duration: DURATA_PC_MS});
			else {
				c.target = new Float32Array(t2);
				c.position = new Float32Array(p2);
			}
		} catch {
			/* ignora */
		}
	}
}
/** nome vecchio (EidosTotem): stessa funzione */
export const alzaSopraPrezzo = inquadraPC;

/** da chiamare una volta sul computer (autoAdjust del visore spento): inquadriamo noi dopo ogni calcolo */
export function avviaAlzaPC() {
	if (pcAttivo) return;
	pcAttivo = true;
	osservaMisuraVisore();
	const rifai = () => {
		tUltimoCalcolo = Date.now();
		setDopoCalcolo = false;
		for (const t of timersPC) window.clearTimeout(t);
		timersPC.length = 0;
		// (6 ott pomeriggio, revisione) la geometria nuova entra nella scena alla fine del calcolo (e un cambio del
		// riquadro della scena fa ripartire questa serie): tre passaggi ravvicinati, ognuno non fa nulla se la camera
		// e' gia' a posto. Si FORZA solo se il tocco del cliente e' PRECEDENTE al calcolo (come faceva il visore);
		// se il cliente ruota il gioiello DOPO il calcolo, non glielo rimettiamo a posto sotto le dita.
		// Un ultimo passaggio a 6 s solo se non abbiamo ancora mosso la camera (fascia non misurabile all'avvio).
		const passo = (ultimo: boolean) => inquadraPC(ultimoTocco < tUltimoCalcolo, ultimo);
		for (const ms of [250, 800, 1600]) timersPC.push(window.setTimeout(() => passo(false), ms));
		timersPC.push(
			window.setTimeout(() => {
				if (!setDopoCalcolo) passo(true);
			}, 6000),
		);
	};
	try {
		tokenPC = addListener(EVENTTYPE.SCENE.SCENE_BOUNDING_BOX_CHANGE, rifai);
		tokenPC2 = addListener(EVENTTYPE.SESSION.SESSION_CUSTOMIZED, rifai);
	} catch {
		tokenPC = null;
	}
	try {
		// zoom fatto da altri (pulsante "inquadra" della barra del visore): alla fine della sua animazione si
		// rimette la piastrina al centro. Dopo un movimento del CLIENTE non si fa nulla (ditoRecente); dopo la nostra
		// animazione la camera e' gia' a posto e non succede nulla
		tokenPC3 = addListener(EVENTTYPE_CAMERA.CAMERA_END, () => {
			window.setTimeout(() => inquadraPC(), 80);
		});
		tokenPC4 = addListener(EVENTTYPE_CAMERA.CAMERA_START, () => {
			movimentiCamera++;
		});
	} catch {
		tokenPC3 = null;
	}
	// all'avvio: quando scena, contorni e visore ci sono, la prima inquadratura (il visore non la fa piu' da solo)
	const inizio = Date.now();
	const attesa = window.setInterval(() => {
		let pronto = false;
		try {
			const bb = sceneTree.root.boundingBox;
			const dim = dimensioniVisore();
			pronto = !!bb && !bb.isEmpty() && !!forme && dim.w > 100 && dim.h > 100;
		} catch {
			pronto = false;
		}
		if (pronto || Date.now() - inizio > 20000 || !pcAttivo) {
			window.clearInterval(attesa);
			if (pcAttivo) rifai();
		}
	}, 400);
}

export function fermaAlzaPC() {
	pcAttivo = false;
	for (const t of timersPC) window.clearTimeout(t);
	timersPC.length = 0;
	for (const tk of [tokenPC, tokenPC2, tokenPC3, tokenPC4]) {
		if (!tk) continue;
		try {
			removeListener(tk);
		} catch {
			/* ignora */
		}
	}
	tokenPC = null;
	tokenPC2 = null;
	tokenPC3 = null;
	tokenPC4 = null;
}

// per le prove dal browser (console): window.__eidosDedica.box() / .inquadra() / .on() / .off(1.5)
(window as unknown as {__eidosDedica: unknown}).__eidosDedica = {
	box: () => {
		const b = riquadroPiastrina();
		const bb = sceneTree.root.boundingBox;
		return {scena: bb ? [Array.from(bb.min), Array.from(bb.max)] : null, piastrina: b ? [Array.from(b.min), Array.from(b.max)] : null};
	},
	inquadra: inquadraPiastrina,
	on: zoomDedicaOn,
	off: zoomDedicaOff,
	attivo: () => attivo,
	camere: () => camere().map((c) => ({fattore: c.zoomToFactor})),
	totem: () => inquadraTotem(),
	pc: () => inquadraPC(true),
	banda: () => bandaLibera(),
	diario: () => diario.slice(),
	canvas: () => {
		const c = canvasVisore();
		return c ? {cw: c.clientWidth, ch: c.clientHeight, w: c.width, h: c.height, cls: c.className, connesso: c.isConnected, id: c.id, genitore: c.parentElement?.className} : null;
	},
	stato: () => ({
		ditoGiu,
		ultimoTocco,
		formeCaricate: !!forme,
		mis: misuraPiastrina(),
		riquadroTotem: (() => {
			const b = riquadroTotem();
			return b ? [Array.from(b.min), Array.from(b.max)] : null;
		})(),
		totemAttivo,
		pcAttivo,
		pcAscolti: [!!tokenPC, !!tokenPC2, !!tokenPC3, !!tokenPC4],
		movimentiCamera,
		ultimoSetPC,
		dedicaAttiva: attivo,
	}),
};
